import { useState } from "react";
import { useLocation } from "wouter";
import type { PlanEvent, Task } from "../../../shared/types";
import { HeroPreview } from "./HeroPreview";
import { track } from "../../../lib/analytics";
import { APP_VERSION, APP_RELEASES } from "../../../shared/version";
import { SparklesIcon } from "../../../ui/icons";

function AndroidIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
      <path d="M17.523 15.341a1.004 1.004 0 0 0 0-2.008 1.004 1.004 0 0 0 0 2.008m-11.046 0a1.004 1.004 0 0 0 0-2.008 1.004 1.004 0 0 0 0 2.008m11.405-6.02 2.005-3.473a.416.416 0 0 0-.152-.567.416.416 0 0 0-.567.152l-2.03 3.517A12.2 12.2 0 0 0 12 7.816a12.2 12.2 0 0 0-5.138 1.134L4.832 5.433a.416.416 0 0 0-.567-.152.416.416 0 0 0-.152.567l2.005 3.473C2.685 11.204.343 14.57 0 18.5h24c-.344-3.93-2.686-7.296-6.118-9.179" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11" />
    </svg>
  );
}

interface HeroSectionProps {
  readonly todayEvents: PlanEvent[];
  readonly doingTasks: Task[];
}

function formatDateFr(date: string): string {
  const [year, month, day] = date.split("-");
  const months = ["jan.", "fév.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  return `${Number(day)} ${months[Number(month) - 1]} ${year}`;
}

function VersionBadge({ navigate }: { readonly navigate: (to: string) => void }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const latest = APP_RELEASES[0];

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        onClick={() => navigate("/releases")}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg
          border border-border bg-surface-alt/50 text-xs text-ink-muted hover:text-accent
          hover:border-accent/30 transition-colors"
      >
        <span className="w-1 h-1 rounded-full bg-green-500" />
        v{APP_VERSION}
      </button>

      {showTooltip && latest && (
        <div className="absolute top-full left-0 mt-2 z-50 w-64 animate-fade-in">
          <div className="bg-surface border border-border rounded-xl shadow-2xl p-4 backdrop-blur-xl">
            <div className="flex items-center gap-2 mb-2.5">
              <SparklesIcon className="w-3.5 h-3.5 text-accent" />
              <span className="text-xs font-bold text-ink">v{latest.version}</span>
              <span className="text-[10px] text-ink-faint ml-auto">{formatDateFr(latest.date)}</span>
            </div>
            <ul className="flex flex-col gap-1.5">
              {latest.changes.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-[11px] text-ink-muted leading-relaxed">
                  <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full bg-accent" />
                  {c}
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/releases")}
              className="mt-3 pt-2 border-t border-border/50 w-full text-left text-[10px] text-accent font-medium hover:text-accent/80 transition-colors"
            >
              Voir toutes les nouveautés →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function HeroSection({ todayEvents, doingTasks }: HeroSectionProps) {
  const [, navigate] = useLocation();

  return (
    <section className="relative pt-28 pb-12 px-6 overflow-hidden">
      <div
        className="absolute top-[-10%] right-[-5%] w-[450px] h-[450px] rounded-full
          bg-accent/10 blur-[120px] animate-[landing-float_10s_ease-in-out_infinite]
          pointer-events-none"
      />

      <div className="relative z-10 max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-16">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg
                border border-accent/20 bg-accent/5 text-sm text-accent font-medium"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              Bêta ouverte — gratuit pendant la bêta
            </div>
            <VersionBadge navigate={navigate} />
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight text-ink">
            Votre productivité,
            <br />
            simplifiée.
          </h1>

          <p className="mt-6 text-lg text-ink-muted max-w-lg leading-relaxed">
            Fini de jongler entre cinq apps. Palnia réunit vos tâches, votre
            calendrier, vos habitudes et vos timers — sans superflu,
            et vos données restent chiffrées.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => { track("landing_cta_clicked", { cta: "register" }); navigate("/register"); }}
              className="px-7 py-3.5 text-base font-semibold text-white bg-accent
                hover:bg-accent-hover rounded-xl shadow-lg shadow-accent/15
                transition-all hover:-translate-y-0.5 active:translate-y-0"
            >
              Essayer maintenant
            </button>
            <button
              onClick={() => { track("landing_cta_clicked", { cta: "login" }); navigate("/login"); }}
              className="px-4 py-3.5 text-base font-medium text-ink-muted
                hover:text-accent transition-colors"
            >
              Se connecter →
            </button>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <span className="text-xs text-ink-faint">Disponible sur</span>
            <div className="flex items-center gap-2">
              <a
                href="https://palnia.newalfox.fr/downloads/palnia.apk"
                onClick={() => track("landing_download_clicked", { platform: "android" })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                  border border-border bg-surface-alt/50 text-xs text-ink-muted
                  hover:text-ink hover:border-border-strong transition-colors"
              >
                <AndroidIcon />
                Android
              </a>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                  border border-border/50 bg-surface-alt/30 text-xs text-ink-faint cursor-default"
                title="Disponible après la bêta avec l'abonnement"
              >
                <AppleIcon />
                iOS — après la bêta
              </span>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 hidden md:block">
          <HeroPreview todayEvents={todayEvents} doingTasks={doingTasks} />
        </div>
      </div>
    </section>
  );
}
